import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ui/ErrorBoundary';
import logApi from './services/api/logError';
import { MsalProvider } from "./auth/msalExports";
import store from './ducks';
import * as SessionHandler from './services/sessionHandler';

const isAuthBypass = process.env.REACT_APP_AUTH_BYPASS === 'true';

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 *
 * In AUTH BYPASS mode (Docker dev), a lightweight mock instance is used instead.
 */
let msalInstance;
if (isAuthBypass) {
    msalInstance = require('./auth/mockMsalInstance').default;
} else {
    const { PublicClientApplication } = require('@azure/msal-browser');
    const { msalConfig } = require('./msalAuthConfig');
    msalInstance = new PublicClientApplication(msalConfig);
}

export { msalInstance };

const rootElement = document.getElementById('root');
const root = ReactDOM.createRoot(rootElement);

// Global error listeners for full coverage
const renderGlobalErrorPage = async () => {
    rootElement.innerHTML = "";
    const errorDiv = document.createElement('div');
    rootElement.appendChild(errorDiv);
    let redirectURL = `${window.location.origin}/error-session-expired`;;
    await SessionHandler.handleLogout(redirectURL);
};

window.onerror = function async(message, source, lineno, colno, error) {
    logApi.logErrorToServer(error || { message, source, lineno, colno });
    renderGlobalErrorPage();
};

window.onunhandledrejection = function async(event) {
    logApi.logErrorToServer(event.reason);
    renderGlobalErrorPage();
};

try {
    root.render(
        <React.StrictMode>
            <Provider store={store}>
                <MsalProvider instance={msalInstance}>
                    <ErrorBoundary>
                        <App />
                    </ErrorBoundary>
                </MsalProvider>
            </Provider>
        </React.StrictMode>
    );
} catch (error) {
    logApi.logErrorToServer(error);
    renderGlobalErrorPage();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
